"use client";
import React, { useEffect, useState } from "react";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import Button from "../../../../components/Button";
import Dropdown from "../../../../components/Dropdown";
import styles from "../css/dispense-table.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchProfileInfo, fetchProfileFamily, fetchPatientScripts, fetchScriptDetails } from "../../../../api/dispensary/script/processScriptApi.js";
import { fetchPatientOtcScripts } from "../../../../api/otcDirect/otcDirectApi.js";

const initialData = [
  { scriptNo: "S001", date: "2025-09-01", costPrice: 150.5, paymentType: "Cash" },
  { scriptNo: "S002", date: "2025-09-02", costPrice: 275.0, paymentType: "Card" },
  { scriptNo: "S003", date: "2025-09-05", costPrice: 320.99, paymentType: "Medical Aid" },
  { scriptNo: "S001", date: "2025-09-10", costPrice: 99.0, paymentType: "Cash" },
  { scriptNo: "S002", date: "2025-09-12", costPrice: 210.0, paymentType: "Card" },
  { scriptNo: "S003", date: "2025-09-15", costPrice: 450.75, paymentType: "Medical Aid" },
];

export default function DispenseTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(initialData);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPatient, setSelectedPatient] = useState(""); // stores patientId (string)
  const [provider, setProvider] = useState("");
  const [aidNumber, setAidNumber] = useState("");
  const [patientOptions, setPatientOptions] = useState([]); // [{ value, label, enabledbeneficiary }]
  const [practitioner, setPractitioner] = useState("");
  const [mainMemberEnabled, setMainMemberEnabled] = useState(false);
  const [mainPatientId, setMainPatientId] = useState(null); // string id of main member
  const [patientEnableBeneficiary, setPatientEnableBeneficiary] = useState(false);

  const itemsPerPage = 5;

  // Load main member context (profileNo) if provided from OTCCustomerD
  useEffect(() => {
    const profileNo = location.state?.profileNo;
    if (!profileNo) return;

    const load = async () => {
      try {
        const info = await fetchProfileInfo(profileNo);
        if (info) {
          const { medicalAidProvider, medicalAidNumber, practitioner: infoPractitioner, mainPatient, enabled } = info;
          setProvider(medicalAidProvider || "");
          setAidNumber(medicalAidNumber || "");
          setMainMemberEnabled(enabled || false);
          if (mainPatient?.id != null) {
            const mainIdStr = mainPatient.id?.toString?.() ?? String(mainPatient.id);
            setMainPatientId(mainIdStr);
          }

          const p = infoPractitioner || mainPatient?.practitioner || null;
          if (p) {
            const titlePart = p.title ? `${p.title} ` : "";
            const namePart = [p.surname, p.firstname].filter(Boolean).join(" ");
            const display = `${titlePart}${namePart}`.trim();
            if (display) setPractitioner(display);
          }
        }

        const family = await fetchProfileFamily(profileNo);
        const options = (family || []).map((m) => ({
          value: String(m.id),
          label: m.label,
          enabledbeneficiary: m.enabledbeneficiary,
        }));
        if (options.length > 0) {
          setPatientOptions(options);
          // Preselect main member (API usually returns main first)
          const firstValue = options[0].value;
          setSelectedPatient(firstValue);
          // Determine initial enable state based on profile + beneficiary logic
          const isBeneficiary = mainPatientId ? firstValue !== mainPatientId : false;
          if (!mainMemberEnabled) {
            // If the profile is disabled, block both main member and beneficiaries
            setPatientEnableBeneficiary(false);
          } else if (isBeneficiary) {
            // Profile is enabled, so beneficiaries depend on enabledbeneficiary
            setPatientEnableBeneficiary(options[0].enabledbeneficiary || false);
          } else {
            // Main member uses profile enabled flag
            setPatientEnableBeneficiary(true);
          }
        }
      } catch (e) {
        console.error("Failed to load profile context for OTC:", e);
      }
    };
    load();
  }, [location.state]);

  // Keep patientEnableBeneficiary in sync when selection or family/main state changes
  useEffect(() => {
    if (!selectedPatient || !patientOptions.length) return;
    const selected = patientOptions.find((o) => o.value === selectedPatient);
    const isBeneficiary = mainPatientId ? selectedPatient !== mainPatientId : false;
    if (!mainMemberEnabled) {
      // If the profile is disabled, block both main member and beneficiaries
      setPatientEnableBeneficiary(false);
    } else if (isBeneficiary) {
      // Profile is enabled, so beneficiaries depend on enabledbeneficiary
      setPatientEnableBeneficiary(selected?.enabledbeneficiary || false);
    } else {
      // Main member uses profile enabled flag
      setPatientEnableBeneficiary(true);
    }
  }, [selectedPatient, patientOptions, mainPatientId, mainMemberEnabled]);

  // Fetch OTC scripts for selected patient
  useEffect(() => {
    const fetchScripts = async () => {
      if (!selectedPatient) return;
      try {
        const scripts = await fetchPatientOtcScripts(selectedPatient);
        const all = scripts || [];
        const mapped = all.map((s, idx) => ({
          scriptNo: s.scriptNo || s.id || `S${idx + 1}`,
          date: s.date || s.createdAt || "",
          costPrice: s.total || s.amount || 0,
          paymentType: s.paymentType || "Cash",
        }));
        setData(mapped);
        setCurrentPage(1);
      } catch (err) {
        console.error("Failed to load patient scripts:", err);
      }
    };
    fetchScripts();
  }, [selectedPatient]);

  // Refresh practitioner when patient changes from that patient's latest script
  useEffect(() => {
    if (!selectedPatient) return;
    const loadPractitioner = async () => {
      try {
        // Try derive from patient's latest script first
        const scripts = await fetchPatientScripts(selectedPatient);
        const first = Array.isArray(scripts) && scripts.length > 0 ? scripts[0] : null;
        if (first?.scriptNo) {
          const details = await fetchScriptDetails(first.scriptNo);
          const p = details?.practitioner || null;
          if (p) {
            const titlePart = p.title ? `${p.title} ` : "";
            const namePart = [p.surname, p.firstname].filter(Boolean).join(" ");
            const display = `${titlePart}${namePart}`.trim();
            if (display) {
              setPractitioner(display);
              return;
            }
          }
        }
        // If none found, clear to avoid misleading value
        setPractitioner("");
      } catch (e) {
        // silent
      }
    };
    loadPractitioner();
  }, [selectedPatient, location.state]);

  const handleViewClick = (row) => {
    const scriptData = {
      scriptNo: row.scriptNo,
      date: row.date,
      drugs: [],
      paymentType: row.paymentType,
      patient: patientOptions.find((o) => o.value === selectedPatient)?.label || "N/A",
      practitioner: practitioner || "N/A",
      pharmacist: "N/A",
      medicalAid: provider || (row.paymentType === "Medical Aid" ? "Medical Aid" : ""),
      medicalAidNumber: aidNumber || "",
    };
    navigate(`/customer/dispensary/otc-direct/dispense/${row.scriptNo}`, {
      state: { scriptData },
    });
  };

  const handleDispense = async () => {
    const inferredPaymentType = provider || aidNumber ? "Medical Aid" : "Cash";
    let derivedPract = "";
    try {
      // Derive at click time to avoid stale state
      if (selectedPatient) {
        const scripts = await fetchPatientScripts(selectedPatient);
        const first = Array.isArray(scripts) && scripts.length > 0 ? scripts[0] : null;
        if (first?.scriptNo) {
          const details = await fetchScriptDetails(first.scriptNo);
          const p = details?.practitioner || null;
          if (p) {
            const titlePart = p.title ? `${p.title} ` : "";
            const namePart = [p.surname, p.firstname].filter(Boolean).join(" ");
            const display = `${titlePart}${namePart}`.trim();
            if (display) derivedPract = display;
          }
        }
      }
    } catch {}
    navigate("/customer/dispensary/otc-direct/medication", {
      state: {
        preselectedPatient: patientOptions.find((o) => o.value === selectedPatient)?.label || "",
        preselectedPractitioner: derivedPract,
        preselectedPaymentType: inferredPaymentType,
        disablePatient: true,
        disablePractitioner: !!derivedPract,
        profileNo: location.state?.profileNo,
        patientId: selectedPatient,
      },
    });
  };

  const columns = [
    { header: "Date", accessor: "date" },
    { header: "Cost Price (R)", accessor: "costPrice" },
    { header: "Payment Type", accessor: "paymentType" },
    {
      header: "Action",
      accessor: "action",
      render: (value, row) => (
        <Button
          variant="primary"
          onClick={() => handleViewClick(row)}
          className={styles.tableActionBtn}
        >
          View
        </Button>
      ),
    },
  ];

  const handlePageChange = (page) => setCurrentPage(page);
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className={styles.container}>
      <Navbar />
      <main className={styles.main}>
        <h1 className={styles.title}>Dispensed OTC Records</h1>
        <div className={styles.formWrapper}>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Patient</label>
            <Dropdown
              className={styles.dropdownField}
              name="patient"
              options={patientOptions.map((o) => o.label)}
              value={patientOptions.find((o) => o.value === selectedPatient)?.label || ""}
              onChange={(val) => {
                const found = patientOptions.find((o) => o.label === val);
                setSelectedPatient(found ? found.value : "");
              }}
              placeholder="Select a patient"
            />
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Medical Aid Provider</label>
            <input
              type="text"
              className={styles.inputField}
              value={provider}
              readOnly={true}
              placeholder="Enter provider"
            />
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Medical Aid Number</label>
            <input
              type="text"
              className={styles.inputField}
              value={aidNumber}
              readOnly={true}
              placeholder="Enter aid number"
            />
          </div>
          <Button onClick={handleDispense} variant="primary" disabled={!patientEnableBeneficiary}>
            Dispense
          </Button>
        </div>
        <Table columns={columns} data={paginatedData} />
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}